import { Injectable, signal, computed } from '@angular/core';
import {
  ReportStatus,
  FaaGeneratedReport,
  StateGeneratedReport,
  FaaReportOptions,
  StateReportOptions,
  FAA_REPORT_TYPES,
  STATE_REPORT_TYPES
} from '../models/report.model';

/**
 * DTOs for creating reports
 */
export interface CreateFaaReportDto {
  reportTypeValue: string;
  startDate: string;
  endDate: string;
  options: FaaReportOptions;
}

export interface CreateStateReportDto {
  reportTypeValue: string;
  stateCode: string;
  startDate: string;
  endDate: string;
  options: StateReportOptions;
}

/**
 * State options for state reports
 */
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'IA', label: 'Iowa' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MT', label: 'Montana' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'WI', label: 'Wisconsin' }
] as const;

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  // Signal-based state for FAA reports
  private faaReportsSignal = signal<FaaGeneratedReport[]>([]);
  private faaLoadingSignal = signal<boolean>(false);
  private faaErrorSignal = signal<string | null>(null);

  // Signal-based state for State reports
  private stateReportsSignal = signal<StateGeneratedReport[]>([]);
  private stateLoadingSignal = signal<boolean>(false);
  private stateErrorSignal = signal<string | null>(null);

  // Public computed signals for FAA reports
  readonly faaReports = computed(() => this.faaReportsSignal());
  readonly isFaaLoading = computed(() => this.faaLoadingSignal());
  readonly faaError = computed(() => this.faaErrorSignal());

  // Public computed signals for State reports
  readonly stateReports = computed(() => this.stateReportsSignal());
  readonly isStateLoading = computed(() => this.stateLoadingSignal());
  readonly stateError = computed(() => this.stateErrorSignal());

  // Report type options
  readonly faaReportTypes = FAA_REPORT_TYPES;
  readonly stateReportTypes = STATE_REPORT_TYPES;
  readonly usStates = US_STATES;

  constructor() {
    this.loadMockFaaReports();
    this.loadMockStateReports();
  }

  private loadMockFaaReports(): void {
    const mockReports: FaaGeneratedReport[] = [
      {
        id: 'faa-1',
        reportType: 'Complete FAA Report',
        reportTypeValue: 'all',
        dateRange: 'Nov 1, 2024 - Nov 30, 2024',
        startDate: '2024-11-01',
        endDate: '2024-11-30',
        generatedAt: '2024-12-01T10:30:00',
        status: 'ready',
        recordCount: 156,
        fileSize: '2.4 MB',
        pdfUrl: 'https://s3.amazonaws.com/adn-reports/faa-report-001.pdf',
        pdfGeneratedAt: '2024-12-01T10:32:00',
        options: {
          includeFlightLogs: true,
          includeSprayRecords: true,
          includePilotInfo: true,
          includeAircraftInfo: true
        }
      },
      {
        id: 'faa-2',
        reportType: 'Part 137 Operations',
        reportTypeValue: 'part137',
        dateRange: 'Oct 1, 2024 - Oct 31, 2024',
        startDate: '2024-10-01',
        endDate: '2024-10-31',
        generatedAt: '2024-11-02T14:15:00',
        status: 'submitted',
        recordCount: 89,
        fileSize: '1.1 MB',
        pdfUrl: 'https://s3.amazonaws.com/adn-reports/faa-report-002.pdf',
        pdfGeneratedAt: '2024-11-02T14:18:00',
        submittedAt: '2024-11-05T09:30:00',
        submittedTo: 'faa-compliance@faa.gov',
        options: {
          includeFlightLogs: true,
          includeSprayRecords: true,
          includePilotInfo: true,
          includeAircraftInfo: false
        }
      },
      {
        id: 'faa-3',
        reportType: 'Waiver Operations',
        reportTypeValue: 'waiver',
        dateRange: 'Sep 15, 2024 - Dec 15, 2024',
        startDate: '2024-09-15',
        endDate: '2024-12-15',
        generatedAt: '2024-12-15T09:00:00',
        status: 'generating',
        recordCount: 0,
        generationProgress: 65,
        options: {
          includeFlightLogs: true,
          includeSprayRecords: false,
          includePilotInfo: true,
          includeAircraftInfo: true
        }
      },
      {
        id: 'faa-4',
        reportType: 'Complete FAA Report',
        reportTypeValue: 'all',
        dateRange: 'Aug 1, 2024 - Aug 31, 2024',
        startDate: '2024-08-01',
        endDate: '2024-08-31',
        generatedAt: '2024-09-01T11:45:00',
        status: 'draft',
        recordCount: 124,
        options: {
          includeFlightLogs: true,
          includeSprayRecords: true,
          includePilotInfo: false,
          includeAircraftInfo: false
        }
      },
      {
        id: 'faa-5',
        reportType: 'Part 107 Compliance',
        reportTypeValue: 'part107',
        dateRange: 'Jul 1, 2024 - Jul 31, 2024',
        startDate: '2024-07-01',
        endDate: '2024-07-31',
        generatedAt: '2024-08-01T08:00:00',
        status: 'failed',
        recordCount: 0,
        options: {
          includeFlightLogs: true,
          includeSprayRecords: true,
          includePilotInfo: true,
          includeAircraftInfo: true
        }
      }
    ];

    this.faaReportsSignal.set(mockReports);
  }

  private loadMockStateReports(): void {
    const mockReports: StateGeneratedReport[] = [
      {
        id: 'state-1',
        reportType: 'Pesticide Application Report',
        reportTypeValue: 'pesticide',
        state: 'Texas',
        stateCode: 'TX',
        dateRange: 'Nov 1, 2024 - Nov 30, 2024',
        startDate: '2024-11-01',
        endDate: '2024-11-30',
        generatedAt: '2024-12-01T10:30:00',
        status: 'ready',
        recordCount: 78,
        fileSize: '1.8 MB',
        pdfUrl: 'https://s3.amazonaws.com/adn-reports/state-report-001.pdf',
        pdfGeneratedAt: '2024-12-01T10:33:00',
        options: {
          includeChemicals: true,
          includeApplicationRates: true,
          includeFieldMaps: true,
          includeWeatherData: false
        }
      },
      {
        id: 'state-2',
        reportType: 'Complete State Report',
        reportTypeValue: 'all',
        state: 'Oklahoma',
        stateCode: 'OK',
        dateRange: 'Oct 1, 2024 - Oct 31, 2024',
        startDate: '2024-10-01',
        endDate: '2024-10-31',
        generatedAt: '2024-11-02T14:15:00',
        status: 'submitted',
        recordCount: 45,
        fileSize: '2.1 MB',
        pdfUrl: 'https://s3.amazonaws.com/adn-reports/state-report-002.pdf',
        pdfGeneratedAt: '2024-11-02T14:18:00',
        submittedAt: '2024-11-05T11:00:00',
        submittedTo: 'pesticide.reporting@ok.gov',
        options: {
          includeChemicals: true,
          includeApplicationRates: true,
          includeFieldMaps: true,
          includeWeatherData: true
        }
      },
      {
        id: 'state-3',
        reportType: 'Fertilizer Application Report',
        reportTypeValue: 'fertilizer',
        state: 'Kansas',
        stateCode: 'KS',
        dateRange: 'Sep 15, 2024 - Dec 15, 2024',
        startDate: '2024-09-15',
        endDate: '2024-12-15',
        generatedAt: '2024-12-15T09:00:00',
        status: 'generating',
        recordCount: 0,
        generationProgress: 42,
        options: {
          includeChemicals: false,
          includeApplicationRates: true,
          includeFieldMaps: true,
          includeWeatherData: false
        }
      },
      {
        id: 'state-4',
        reportType: 'State Compliance Summary',
        reportTypeValue: 'compliance',
        state: 'Texas',
        stateCode: 'TX',
        dateRange: 'Jan 1, 2024 - Dec 31, 2024',
        startDate: '2024-01-01',
        endDate: '2024-12-31',
        generatedAt: '2024-12-10T11:45:00',
        status: 'draft',
        recordCount: 312,
        options: {
          includeChemicals: true,
          includeApplicationRates: true,
          includeFieldMaps: false,
          includeWeatherData: false
        }
      },
      {
        id: 'state-5',
        reportType: 'Drift Mitigation Report',
        reportTypeValue: 'drift',
        state: 'California',
        stateCode: 'CA',
        dateRange: 'Aug 1, 2024 - Aug 31, 2024',
        startDate: '2024-08-01',
        endDate: '2024-08-31',
        generatedAt: '2024-09-01T08:00:00',
        status: 'failed',
        recordCount: 0,
        options: {
          includeChemicals: true,
          includeApplicationRates: true,
          includeFieldMaps: true,
          includeWeatherData: true
        }
      }
    ];

    this.stateReportsSignal.set(mockReports);
  }

  // ============================================
  // FAA REPORT METHODS
  // ============================================

  getFaaReports(): FaaGeneratedReport[] {
    return this.faaReportsSignal();
  }

  getFaaReportById(id: string): FaaGeneratedReport | undefined {
    return this.faaReportsSignal().find(r => r.id === id);
  }

  createFaaReport(dto: CreateFaaReportDto): FaaGeneratedReport {
    const reportTypeLabel = this.faaReportTypes.find(t => t.value === dto.reportTypeValue)?.label || 'Report';

    const newReport: FaaGeneratedReport = {
      id: `faa-${Date.now()}`,
      reportType: reportTypeLabel,
      reportTypeValue: dto.reportTypeValue,
      dateRange: this.formatDateRange(dto.startDate, dto.endDate),
      startDate: dto.startDate,
      endDate: dto.endDate,
      generatedAt: new Date().toISOString(),
      status: 'generating',
      recordCount: 0,
      generationProgress: 0,
      options: dto.options
    };

    this.faaReportsSignal.update(reports => [newReport, ...reports]);
    this.simulateReportGeneration(newReport, 'faa');
    return newReport;
  }

  generateFaaPdf(reportId: string): void {
    const report = this.getFaaReportById(reportId);
    if (!report || report.status !== 'draft') return;

    this.updateFaaReport(reportId, { status: 'generating', generationProgress: 0 });
    this.simulateReportGeneration(report, 'faa');
  }

  updateFaaReport(id: string, updates: Partial<FaaGeneratedReport>): FaaGeneratedReport | null {
    const index = this.faaReportsSignal().findIndex(r => r.id === id);
    if (index === -1) return null;

    const updatedReport = { ...this.faaReportsSignal()[index], ...updates };
    this.faaReportsSignal.update(reports => {
      const newReports = [...reports];
      newReports[index] = updatedReport;
      return newReports;
    });

    return updatedReport;
  }

  deleteFaaReport(id: string): boolean {
    const initialLength = this.faaReportsSignal().length;
    this.faaReportsSignal.update(reports => reports.filter(r => r.id !== id));
    return this.faaReportsSignal().length < initialLength;
  }

  retryFaaReport(id: string): void {
    const report = this.getFaaReportById(id);
    if (!report || report.status !== 'failed') return;

    this.updateFaaReport(id, { status: 'generating', generationProgress: 0 });
    this.simulateReportGeneration(report, 'faa');
  }

  submitFaaReport(id: string, email: string): void {
    this.updateFaaReport(id, {
      status: 'submitted',
      submittedAt: new Date().toISOString(),
      submittedTo: email
    });
  }

  // ============================================
  // STATE REPORT METHODS
  // ============================================

  getStateReports(): StateGeneratedReport[] {
    return this.stateReportsSignal();
  }

  getStateReportById(id: string): StateGeneratedReport | undefined {
    return this.stateReportsSignal().find(r => r.id === id);
  }

  createStateReport(dto: CreateStateReportDto): StateGeneratedReport {
    const reportTypeLabel = this.stateReportTypes.find(t => t.value === dto.reportTypeValue)?.label || 'Report';
    const stateLabel = this.usStates.find(s => s.value === dto.stateCode)?.label || dto.stateCode;

    const newReport: StateGeneratedReport = {
      id: `state-${Date.now()}`,
      reportType: reportTypeLabel,
      reportTypeValue: dto.reportTypeValue,
      state: stateLabel,
      stateCode: dto.stateCode,
      dateRange: this.formatDateRange(dto.startDate, dto.endDate),
      startDate: dto.startDate,
      endDate: dto.endDate,
      generatedAt: new Date().toISOString(),
      status: 'generating',
      recordCount: 0,
      generationProgress: 0,
      options: dto.options
    };

    this.stateReportsSignal.update(reports => [newReport, ...reports]);
    this.simulateReportGeneration(newReport, 'state');
    return newReport;
  }

  generateStatePdf(reportId: string): void {
    const report = this.getStateReportById(reportId);
    if (!report || report.status !== 'draft') return;

    this.updateStateReport(reportId, { status: 'generating', generationProgress: 0 });
    this.simulateReportGeneration(report, 'state');
  }

  updateStateReport(id: string, updates: Partial<StateGeneratedReport>): StateGeneratedReport | null {
    const index = this.stateReportsSignal().findIndex(r => r.id === id);
    if (index === -1) return null;

    const updatedReport = { ...this.stateReportsSignal()[index], ...updates };
    this.stateReportsSignal.update(reports => {
      const newReports = [...reports];
      newReports[index] = updatedReport;
      return newReports;
    });

    return updatedReport;
  }

  deleteStateReport(id: string): boolean {
    const initialLength = this.stateReportsSignal().length;
    this.stateReportsSignal.update(reports => reports.filter(r => r.id !== id));
    return this.stateReportsSignal().length < initialLength;
  }

  retryStateReport(id: string): void {
    const report = this.getStateReportById(id);
    if (!report || report.status !== 'failed') return;

    this.updateStateReport(id, { status: 'generating', generationProgress: 0 });
    this.simulateReportGeneration(report, 'state');
  }

  submitStateReport(id: string, email: string): void {
    this.updateStateReport(id, {
      status: 'submitted',
      submittedAt: new Date().toISOString(),
      submittedTo: email
    });
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  private formatDateRange(start: string, end: string): string {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
  }

  private simulateReportGeneration(report: FaaGeneratedReport | StateGeneratedReport, type: 'faa' | 'state'): void {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 15 + 5;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);

        const completedUpdate = {
          status: 'ready' as ReportStatus,
          generationProgress: 100,
          recordCount: Math.floor(Math.random() * 100) + 30,
          fileSize: `${(Math.random() * 3 + 0.5).toFixed(1)} MB`,
          pdfUrl: `https://s3.amazonaws.com/adn-reports/${type}-report-${report.id}.pdf`,
          pdfGeneratedAt: new Date().toISOString()
        };

        if (type === 'faa') {
          this.updateFaaReport(report.id, completedUpdate);
        } else {
          this.updateStateReport(report.id, completedUpdate);
        }
      } else {
        if (type === 'faa') {
          this.updateFaaReport(report.id, { generationProgress: Math.floor(progress) });
        } else {
          this.updateStateReport(report.id, { generationProgress: Math.floor(progress) });
        }
      }
    }, 500);
  }

  getStatusText(status: ReportStatus): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'generating': return 'Generating...';
      case 'ready': return 'Ready';
      case 'submitted': return 'Submitted';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  getStatusIcon(status: ReportStatus): string {
    switch (status) {
      case 'draft': return 'bi-file-earmark';
      case 'generating': return 'bi-arrow-repeat';
      case 'ready': return 'bi-check-circle';
      case 'submitted': return 'bi-send-check';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-file-earmark';
    }
  }

  getReportTypeLabel(value: string, type: 'faa' | 'state'): string {
    if (type === 'faa') {
      return this.faaReportTypes.find(t => t.value === value)?.label || value;
    }
    return this.stateReportTypes.find(t => t.value === value)?.label || value;
  }

  getStateLabel(code: string): string {
    return this.usStates.find(s => s.value === code)?.label || code;
  }
}
